import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './Home';
import AdminPanel from './admin/AdminPanel';
import ClientLogin from './client/ClientLogin';
import ClientSignup from './client/ClientSignup';
import ClientPortal from './client/ClientPortal';
import EmployeeLogin from './employee/EmployeeLogin';
import EmployeePortal from './employee/EmployeePortal';
import LoginPage from './pages/LoginPage';
import ServiceDetail from './pages/ServiceDetail';
import ProjectDetail from './admin/ProjectDetail';
import RoomDetail from './admin/RoomDetail';
import NotificationContainer from './components/NotificationContainer';
import { useAuth, useAuthStore } from './stores';
import { useClientAuthStore } from './stores';
import { useEmployeeAuthStore } from './stores';

function App() {
  const { isAuthenticated, isLoading, initialize } = useAuth();
  const isInitialized = useAuthStore(state => state.isInitialized);
  const { isAuthenticated: isClientAuthenticated, initialize: initializeClient } = useClientAuthStore();
  const { isAuthenticated: isEmployeeAuthenticated, initialize: initializeEmployee } = useEmployeeAuthStore();

  useEffect(() => {
    // Initialize authentication state on app startup
    console.log('[App] Initializing authentication...');
    const authState = useAuthStore.getState();
    if (!authState.isInitialized) {
      initialize();
    }

    // Initialize client authentication
    const clientAuthState = useClientAuthStore.getState();
    if (!clientAuthState.isInitialized) {
      initializeClient();
    }

    // Initialize employee authentication
    const employeeAuthState = useEmployeeAuthStore.getState();
    if (!employeeAuthState.isInitialized) {
      initializeEmployee();
    }

    // Set up global 401 error handler
    window.handle401Error = () => {
      console.log('[App] Global 401 handler triggered');
      useAuthStore.getState().handle401Error();
    };

    // Cleanup on unmount
    return () => {
      delete window.handle401Error;
    };
  }, [initialize, initializeClient, initializeEmployee]); // Include initialize in dependencies

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route 
          path="/admin" 
          element={
            isInitialized && isAuthenticated 
            ? (
              <AdminPanel />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/admin/projects/:projectId" 
          element={
            isInitialized && isAuthenticated 
            ? (
              <ProjectDetail />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/admin/projects/:projectId/rooms/:roomId" 
          element={
            isInitialized && isAuthenticated 
            ? (
              <RoomDetail />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/service/:serviceId" element={<ServiceDetail />} />
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/signup" element={<ClientSignup />} />
        <Route 
          path="/client/dashboard" 
          element={
            isClientAuthenticated ? (
              <ClientPortal />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="/employee/login" element={<EmployeeLogin />} />
        <Route 
          path="/employee/dashboard" 
          element={
            isEmployeeAuthenticated ? (
              <EmployeePortal />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <NotificationContainer />
    </Router>
  );
}

export default App;
